import React, { useState, FC } from 'react'
import { CTAProps } from './CTA.types'
import { sanitizeHTML } from '../../../utils/common-utils'
import { getValueListBgColor } from '../../../utils/background-util'
import { ErrorBoundary } from '../../ErrorBoundary/src'
import { BrandIcon } from '../../Compounds/src/BrandIconCompound'

/**
 * CTA (Call To Action) component.
 *
 * @remarks
 * Renders a styled call-to-action block with headline, heading, body, and a link.
 * Handles hover and focus states to visually darken or lighten the background.
 * Applies background color and column size based on props.
 *
 * @param props - The properties for the CTA component.
 * @returns The rendered CTA component.
 */
export const CTA: FC<CTAProps> = props => {
  const { content, testId } = props
  const [isHover, setIsHover] = useState(false)
  const {
    body,
    bgcolor,
    headline,
    heading,
    linkHref,
    linkTarget,
    analytics,
    columnSize,
    brandIcon,
  } = content

  const handleDarken = () => {
    setIsHover(true)
  }

  const handleLighten = () => {
    setIsHover(false)
  }

  let bgColorClass = getValueListBgColor(bgcolor ?? 'stone')
  const colSize = columnSize ? `u-size${columnSize}` : ''

  return (
    <ErrorBoundary>
      <div className={`u-sizeConstrained ${colSize}`}>
        <div
          className={`Cta u-posRelative ${bgColorClass}`}
          data-testid={testId ? `${testId}` : null}
        >
          <div className={`Cta-background ${isHover ? 'active' : ''}`}></div>
          <a
            className='Link--stretched'
            href={linkHref}
            target={linkTarget}
            onMouseEnter={handleDarken}
            onMouseLeave={handleLighten}
            onFocus={handleDarken}
            onBlur={handleLighten}
            data-analytics={analytics}
            data-testid={testId ? `${testId}-link` : null}
          >
            <span className='u-hidden'>{heading}</span>
          </a>
          <div className='u-lg-flex u-md-flex u-paddingHoriz40 u-paddingVert20'>
            {brandIcon && (
              <div className='u-flex u-flexAlignItemsCenter u-marginRight6gu'>
                <BrandIcon
                  {...brandIcon}
                  className={`u-marginRight24 ${brandIcon.className ?? ''}`}
                  testId={brandIcon.testId ?? `${testId ?? 'cta'}`}
                  identifier={brandIcon.identifier ?? (testId ? `${testId}-icon` : undefined)}
                />
              </div>
            )}
            <div className='Cta-body u-lg-size3of4 u-md-size3of4'>
              <h5
                className='Cta-superHeadline u-textColorNeutralGrey u-textUppercase u-marginBottom20'
                data-testid={testId ? `${testId}-super-headline` : null}
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(headline ?? '') }}
              ></h5>
              <h3
                className='Cta-heading u-textColorNeutralGrey u-marginBottom8'
                data-testid={testId ? `${testId}-heading` : null}
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(heading ?? '') }}
              ></h3>
              <div
                className='Cta-content Content u-textColorNeutralGrey'
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(body ?? '') }}
                data-testid={testId ? `${testId}-body` : null}
              ></div>
            </div>
            <div className='Cta-link u-size1of4 u-flex u-lg-flexJustifyEnd u-md-flexJustifyEnd u-sm-paddingTop16 u-flexAlignItemsCenter'>
              <span
                className={`Icon u-flex adel-material-symbols u-flexAlignItemsCenter u-flexJustifyCenter u-textColorNeutralGrey ${
                  isHover ? 'active' : ''
                }`}
              >
                chevron_right
              </span>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  )
}
